/**
 * Client-side grouping helpers for V4 contradictions (safety net for near-duplicates).
 */

const STOPWORDS = new Set([
  'a', 'an', 'the', 'and', 'or', 'but', 'is', 'are', 'was', 'were', 'be', 'been', 'being',
  'has', 'have', 'had', 'do', 'does', 'did', 'will', 'would', 'could', 'should', 'may',
  'might', 'must', 'shall', 'can', 'to', 'of', 'in', 'for', 'on', 'with', 'at', 'by',
  'from', 'as', 'into', 'that', 'this', 'it', 'its', 'not', 'no', 'than', 'then',
  'states', 'state', 'stated', 'confirms', 'confirm', 'confirmed', 'reports', 'report',
  'reported', 'said', 'says', 'according', 'claims', 'claim', 'sources', 'source', 'cite',
  'cites', 'cited',
]);

function normalizeCommentKey(comment) {
  return String(comment || '').toLowerCase().replace(/[^a-z0-9\s]/g, ' ').replace(/\s+/g, ' ').trim();
}

function semanticTokens(comment) {
  const tokens = new Set();
  for (const word of normalizeCommentKey(comment).split(' ')) {
    if (word.length >= 2 && !STOPWORDS.has(word)) tokens.add(word);
  }
  return tokens;
}

function tokenJaccard(a, b) {
  if (a.size === 0 && b.size === 0) return 1;
  if (a.size === 0 || b.size === 0) return 0;
  let intersection = 0;
  for (const t of a) if (b.has(t)) intersection++;
  const union = a.size + b.size - intersection;
  return union === 0 ? 0 : intersection / union;
}

export function enrichContradictionSegment(seg) {
  const raw = seg && typeof seg === 'object' ? seg : {};
  const docIdRaw = raw.docId ?? raw.doc_id;
  const segmentIndexRaw = raw.segmentIndex ?? raw.segment_index ?? raw.index;
  let referenceId = String(raw.referenceId ?? raw.reference_id ?? '').trim();
  if (!referenceId && docIdRaw != null && segmentIndexRaw != null) {
    referenceId = `${docIdRaw}:${segmentIndexRaw}`;
  }
  return {
    referenceId,
    text: raw.text ?? raw.snippet ?? '',
    docTitle: raw.docTitle ?? raw.doc_title ?? '',
    docId: docIdRaw != null ? Number(docIdRaw) : undefined,
    segmentId: raw.segmentId != null ? Number(raw.segmentId) : raw.segment_id != null ? Number(raw.segment_id) : undefined,
    segmentIndex: segmentIndexRaw != null ? Number(segmentIndexRaw) : undefined,
    segmentScore: raw.segmentScore != null ? Number(raw.segmentScore) : raw.segment_score != null ? Number(raw.segment_score) : undefined,
  };
}

function mergeSegmentLists(left, right) {
  const byRef = new Map(left.map((s) => [s.referenceId, s]));
  for (const seg of right) {
    const prev = byRef.get(seg.referenceId);
    if (!prev) {
      byRef.set(seg.referenceId, seg);
      continue;
    }
    byRef.set(seg.referenceId, {
      ...prev,
      ...seg,
      text: prev.text || seg.text,
      docTitle: prev.docTitle || seg.docTitle,
      segmentScore: Math.max(prev.segmentScore ?? 0, seg.segmentScore ?? 0),
    });
  }
  return Array.from(byRef.values()).sort((a, b) => (b.segmentScore ?? 0) - (a.segmentScore ?? 0));
}

export function mergeContradictionGroups(items) {
  const working = (items || []).map((item) => ({
    ...item,
    comment: String(item.comment ?? ''),
    segments: [...(item.segments ?? [])].map((s) => enrichContradictionSegment(s)),
  }));

  let merged = true;
  while (merged) {
    merged = false;
    outer: for (let i = 0; i < working.length; i++) {
      const left = working[i];
      const leftTheme = String(left.theme ?? '').trim();
      const leftKey = normalizeCommentKey(left.comment);
      const leftTokens = semanticTokens(left.comment);

      for (let j = i + 1; j < working.length; j++) {
        const right = working[j];
        const rightTheme = String(right.theme ?? '').trim();
        const rightKey = normalizeCommentKey(right.comment);
        const rightTokens = semanticTokens(right.comment);
        const sameTheme = leftTheme.length > 0 && leftTheme === rightTheme;
        const sameComment = leftKey.length > 0 && leftKey === rightKey;
        const similar = tokenJaccard(leftTokens, rightTokens) >= 0.55;

        if (sameTheme || sameComment || similar) {
          left.segments = mergeSegmentLists(left.segments, right.segments);
          if ((right.contradictionScore ?? 0) > (left.contradictionScore ?? 0)) {
            left.contradictionScore = right.contradictionScore;
          }
          if (right.comment.length > left.comment.length) left.comment = right.comment;
          working.splice(j, 1);
          merged = true;
          break outer;
        }
      }
    }
  }

  return working.map((c, i) => ({...c, id: c.id ?? i + 1}));
}

export function parseMetaJson(value, fallback) {
  if (value == null) return fallback;
  if (typeof value === 'string') {
    try {
      return JSON.parse(value);
    } catch {
      return fallback;
    }
  }
  return value;
}
